import { useState, useEffect } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { motion } from 'framer-motion';

function Header() {
  const { t, i18n } = useTranslation();
  const [isMobileView, setIsMobileView] = useState(window.innerWidth < 1167);
  const [isScrolled, setIsScrolled] = useState(false);
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  
  // 1. Estado do Dark Mode puxando do localStorage ou iniciando como 'light'
  const [theme, setTheme] = useState(localStorage.getItem('theme') || 'light');
  
  const location = useLocation();

  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 20);
    };
    const handleResize = () => {
      setIsMobileView(window.innerWidth < 1167);
      if (window.innerWidth >= 1167) {
        setIsMobileMenuOpen(false);
      }
    };
    window.addEventListener('scroll', handleScroll);
    window.addEventListener('resize', handleResize);
    return () => {
      window.removeEventListener('scroll', handleScroll);
      window.removeEventListener('resize', handleResize);
    };
  }, [location]);

  // 2. Efeito do Dark Mode: aplica a classe no HTML e salva no navegador
  useEffect(() => {
    const root = window.document.documentElement;
    if (theme === 'dark') {
      root.classList.add('dark');
    } else {
      root.classList.remove('dark');
    }
    localStorage.setItem('theme', theme);
  }, [theme]);

  // Função para alternar o tema
  const toggleTheme = () => {
    setTheme(theme === 'light' ? 'dark' : 'light');
  };

  const handleLanguageChange = (lang) => {
    i18n.changeLanguage(lang);
  };

  const navLinks = [
    { to: '/', label: t('header.homePage') },
    { to: '/TPQVS', label: isMobileView ? t('header.sigla') : t('header.TPQVS') },
    { to: '/publications', label: t('header.publications') },
    { to: '/team', label: t('header.team') },
    { to: '/about-us', label: t('header.aboutUs') },
  ];

  const isActiveLink = (path) => location.pathname === path;

  return (
    <motion.header
      initial={{ y: -100, opacity: 0 }}
      animate={{ y: 0, opacity: 1 }}
      transition={{ duration: 0.5, ease: 'easeOut' }}
      className={`fixed top-0 left-0 right-0 z-50 transition-all duration-500 ${
        isScrolled
          ? 'bg-glass backdrop-blur-[16px] border-b border-glass-border shadow-glass'
          : 'bg-transparent'
      }`}
    >
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex items-center justify-between h-16 md:h-20">
          {/* Logo */}
          <Link to="/" className="flex items-center gap-3 group">
            <motion.div
              whileHover={{ scale: 1.05, rotate: 5 }}
              transition={{ type: 'spring', stiffness: 400 }}
              className="relative"
            >
              <div className="absolute inset-0 bg-primary/20 rounded-full blur-xl group-hover:bg-primary/40 transition-all duration-300" />
              <img
                src="/img/logo.png"
                alt="Logo"
                className="relative w-12 h-12 md:w-14 md:h-14 object-contain"
              />
            </motion.div>
            <span className="hidden md:block text-xl font-bold text-text-primary group-hover:text-primary transition-colors duration-300">
              {t('header.titleLogo')}
            </span>
          </Link>

          {/* Desktop Navigation */}
          <nav className="hidden lg:flex items-center gap-1">
            {navLinks.map((link) => (
              <Link
                key={link.to}
                to={link.to}
                className={`relative px-4 py-2 text-sm font-medium transition-all duration-300 rounded-lg group ${
                  isActiveLink(link.to)
                    ? 'text-primary'
                    : 'text-text-secondary hover:text-text-primary'
                }`}
              >
                {link.label}
                <span
                  className={`absolute bottom-0 left-1/2 -translate-x-1/2 h-0.5 bg-primary transition-all duration-300 ${
                    isActiveLink(link.to) ? 'w-6' : 'w-0 group-hover:w-6'
                  }`}
                />
              </Link>
            ))}
          </nav>

          {/* Toggles & Mobile Menu Button */}
          <div className="flex items-center gap-3">
            
            {/* 3. Botão do Dark Mode */}
            <motion.button
              whileHover={{ scale: 1.05 }}
              whileTap={{ scale: 0.95 }}
              onClick={toggleTheme}
              className="flex items-center justify-center p-2 w-10 h-10 bg-surface border border-border rounded-lg text-text-secondary hover:text-text-primary hover:border-primary/40 transition-all duration-300"
              aria-label="Alternar Tema"
            >
              {theme === 'light' ? (
                /* Ícone de Lua (Modo Escuro) */
                <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M20.354 15.354A9 9 0 018.646 3.646 9.003 9.003 0 0012 21a9.003 9.003 0 008.354-5.646z" />
                </svg>
              ) : (
                /* Ícone de Sol (Modo Claro) */
                <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 3v1m0 16v1m9-9h-1M4 12H3m15.364 6.364l-.707-.707M6.343 6.343l-.707-.707m12.728 0l-.707.707M6.343 17.657l-.707.707M16 12a4 4 0 11-8 0 4 4 0 018 0z" />
                </svg>
              )}
            </motion.button>

            {/* Language Toggle */}
            <motion.button
              whileHover={{ scale: 1.05 }}
              whileTap={{ scale: 0.95 }}
              onClick={() => handleLanguageChange(i18n.language === 'pt' ? 'en' : 'pt')}
              className="flex items-center gap-2 px-3 py-1.5 h-10 bg-surface border border-border rounded-lg text-sm font-medium text-text-secondary hover:text-text-primary hover:border-primary/40 transition-all duration-300"
            >
              {i18n.language === 'pt' ? (
                <>
                  <span>EN</span>
                  <img src="/img/EUA.png" alt="English" className="w-5 h-5 rounded-sm" />
                </>
              ) : (
                <>
                  <span>PT</span>
                  <img src="/img/BR.png" alt="Portugues" className="w-5 h-5 rounded-sm" />
                </>
              )}
            </motion.button>

            {/* Mobile Menu Button */}
            <motion.button
              whileTap={{ scale: 0.9 }}
              onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
              className="lg:hidden flex flex-col justify-center items-center gap-1.5 p-2 w-10 h-10 rounded-lg hover:bg-surface transition-colors"
              aria-label="Menu"
            >
              <motion.span
                animate={isMobileMenuOpen ? { rotate: 45, y: 8 } : { rotate: 0, y: 0 }}
                className="w-6 h-0.5 bg-text-primary transition-all duration-300"
              />
              <motion.span
                animate={isMobileMenuOpen ? { opacity: 0 } : { opacity: 1 }}
                className="w-6 h-0.5 bg-text-primary transition-all duration-300"
              />
              <motion.span
                animate={isMobileMenuOpen ? { rotate: -45, y: -8 } : { rotate: 0, y: 0 }}
                className="w-6 h-0.5 bg-text-primary transition-all duration-300"
              />
            </motion.button>
          </div>
        </div>
      </div>

      {/* Mobile Menu */}
      <motion.div
        initial={false}
        animate={isMobileMenuOpen ? { height: 'auto', opacity: 1 } : { height: 0, opacity: 0 }}
        transition={{ duration: 0.3 }}
        className="lg:hidden overflow-hidden bg-glass backdrop-blur-[16px] border-t border-glass-border"
      >
        <nav className="flex flex-col p-4 gap-2">
          {navLinks.map((link, index) => (
            <motion.div
              key={link.to}
              initial={{ x: -20, opacity: 0 }}
              animate={isMobileMenuOpen ? { x: 0, opacity: 1 } : { x: -20, opacity: 0 }}
              transition={{ delay: index * 0.1 }}
            >
              <Link
                to={link.to}
                onClick={() => setIsMobileMenuOpen(false)}
                className={`block px-4 py-3 rounded-xl text-base font-medium transition-all duration-300 ${
                  isActiveLink(link.to)
                    ? 'bg-primary/10 text-primary border border-primary/20'
                    : 'text-text-secondary hover:bg-surface hover:text-text-primary'
                }`}
              >
                {link.label}
              </Link>
            </motion.div>
          ))}
        </nav>
      </motion.div>
    </motion.header>
  );
}

export default Header;